import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { locationsData } from '../../data/locationsData';
import { servicesData } from '../../data/servicesData';
import { useModal } from '../../contexts/ModalContext';

const LocationDetailPage = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const { openConsultation } = useModal();
  const location = locationsData[locationId];

  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [locationId]);

  if (!location) {
    return (
      <div className="min-h-screen bg-background flex flex-col justify-center items-center px-6">
        <Icon name="AlertTriangle" size={48} className="text-warning mb-4" />
        <h1 className="text-2xl font-bold mb-2">Location Page Not Found</h1>
        <p className="text-text-secondary mb-6 text-center max-w-md">
          The location directory you are looking for is not configured.
        </p>
        <Button variant="default" onClick={() => navigate('/')}>
          Return Home
        </Button>
      </div>
    );
  }

  // Map related services data
  const matchedServices = (location.relatedServices || [])
    .map(srvId => servicesData[srvId])
    .filter(Boolean);

  // Setup Schemas
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": location.title,
    "description": location.metaDescription,
    "url": `https://bytepairs.com/software-development-company-${location.id}`
  };

  const headquartersAddress = {
    "@type": "PostalAddress",
    "streetAddress": "Hanumandapuram",
    "addressLocality": "Dharapuram",
    "addressRegion": "Tamil Nadu",
    "postalCode": "638656",
    "addressCountry": "IN"
  };

  const headquartersGeo = {
    "@type": "GeoCoordinates",
    "latitude": 10.7386,
    "longitude": 77.5212
  };

  const isDharapuram = location.id === 'dharapuram';

  // Google-compliant schema: LocalBusiness ONLY for real physical Dharapuram HQ;
  // Service + areaServed for regional service coverage locations.
  const structuredDataSchema = isDharapuram
    ? {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Byte Pairs",
        "image": "https://bytepairs.com/android-chrome-512x512.png",
        "url": "https://bytepairs.com/",
        "telephone": "+916374241548",
        "email": "bytepairs2026@gmail.com",
        "priceRange": "₹₹",
        "address": headquartersAddress,
        "geo": headquartersGeo
      }
    : {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": `Custom Software & Web Development in ${location.name}`,
        "description": location.metaDescription,
        "serviceType": "Custom Software Development Services",
        "url": `https://bytepairs.com/software-development-company-${location.id}`,
        "areaServed": {
          "@type": location.id === 'india' ? 'Country' : (location.id === 'tamil-nadu' ? 'State' : 'City'),
          "name": location.name
        },
        "provider": {
          "@type": "LocalBusiness",
          "name": "Byte Pairs",
          "image": "https://bytepairs.com/android-chrome-512x512.png",
          "url": "https://bytepairs.com/",
          "telephone": "+916374241548",
          "email": "bytepairs2026@gmail.com",
          "priceRange": "₹₹",
          "address": headquartersAddress,
          "geo": headquartersGeo
        }
      };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://bytepairs.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Software Development Company",
        "item": `https://bytepairs.com/software-development-company-${location.id}`
      }
    ]
  };

  const faqSchema = location.faq && location.faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": location.faq.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  } : null;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <>
      <Helmet>
        <title>{location.title}</title>
        <meta name="description" content={location.metaDescription} />
        <meta name="keywords" content={location.keywords} />
        <link rel="canonical" href={`https://bytepairs.com/software-development-company-${location.id}`} />

        <meta property="og:title" content={location.title} />
        <meta property="og:description" content={location.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://bytepairs.com/software-development-company-${location.id}`} />
        <meta property="og:image" content="https://bytepairs.com/android-chrome-512x512.png" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={location.title} />
        <meta name="twitter:description" content={location.metaDescription} />
        <meta name="twitter:image" content="https://bytepairs.com/android-chrome-512x512.png" />

        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredDataSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        {faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}
      </Helmet>

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <main className="pt-20">
          {/* Breadcrumbs Section */}
          <div className="max-w-6xl mx-auto px-6 pt-6">
            <nav className="flex items-center space-x-2 text-xs md:text-sm text-text-secondary">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <Icon name="ChevronRight" size={12} />
              <span className="text-foreground font-medium">{location.name} Office Coverage</span>
            </nav>
          </div>

          {/* Hero Section */}
          <section className="relative py-20 bg-gradient-to-br from-primary/5 via-muted/30 to-accent/5 overflow-hidden border-b border-border">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[linear-gradient(rgba(14,165,233,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(14,165,233,0.1)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
            </div>
            <div className="max-w-6xl mx-auto px-6 relative z-10 text-center">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <Icon name="MapPin" size={16} />
                  <span>Regional Operations | {location.name}</span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="text-3xl md:text-5xl lg:text-6xl font-black mb-6 tracking-tight max-w-4xl mx-auto leading-tight"
              >
                Custom Software Engineering in <span className="text-gradient">{location.name}</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed mb-10"
              >
                {location.tagline}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Button
                  variant="default"
                  size="lg"
                  iconName="Calendar"
                  iconPosition="left"
                  onClick={openConsultation}
                  className="bg-gradient-to-r from-primary to-accent text-white shadow-brand-md hover:shadow-brand-lg"
                >
                  Book Free Consultation
                </Button>
                <a href="#solutions" className="inline-flex items-center justify-center border border-border bg-card hover:bg-muted text-foreground px-6 py-3.5 rounded-xl font-semibold transition-all duration-300">
                  <span>Explore Solutions</span>
                </a>
              </motion.div>
            </div>
          </section>

          {/* Overview & Local Industry Section */}
          <section className="py-20 max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              <div className="lg:col-span-5">
                <div className="sticky top-28">
                  <h2 className="text-2xl md:text-3xl font-bold mb-6 tracking-tight">
                    Partnering with Businesses in {location.name}
                  </h2>
                  <p className="text-text-secondary leading-relaxed mb-6 text-sm md:text-base">
                    {location.overview}
                  </p>
                  <div className="p-6 bg-card border border-border rounded-2xl">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-primary/10 text-primary rounded-xl flex items-center justify-center">
                        <Icon name="Code" size={20} />
                      </div>
                      <h4 className="font-semibold text-foreground">Technology Stacks Used</h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {location.relatedTechnologies.map((tech, idx) => (
                        <span key={idx} className="text-xs bg-muted text-text-secondary px-3 py-1.5 rounded-lg border border-border/50 font-medium">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-7">
                <h3 className="text-lg font-bold mb-6 text-primary tracking-wide uppercase">
                  Target Sectors in {location.name}
                </h3>
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-100px' }}
                  className="space-y-6"
                >
                  {location.localIndustries.map((ind, idx) => (
                    <motion.div
                      key={idx}
                      variants={itemVariants}
                      className="p-6 bg-card hover:bg-muted/40 border border-border rounded-2xl transition-all duration-300"
                    >
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 rounded-full bg-accent/10 text-accent flex items-center justify-center mt-1 flex-shrink-0">
                          <span className="text-xs font-bold">{idx + 1}</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-lg mb-2 text-foreground">{ind.name}</h4>
                          <p className="text-text-secondary text-sm md:text-base leading-relaxed">
                            {ind.detail}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </div>
          </section>

          {/* Challenges and Solutions Section */}
          <section id="solutions" className="py-20 bg-muted/30 border-y border-border">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-2xl md:text-4xl font-black tracking-tight mb-4">
                  Challenges & Byte Pairs Solutions
                </h2>
                <p className="text-text-secondary text-sm md:text-base max-w-xl mx-auto">
                  Solving operational friction with clean software layouts and transparent collaboration.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Challenges Card */}
                <div className="p-8 bg-card border border-border rounded-3xl">
                  <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-border">
                    <Icon name="AlertTriangle" size={24} className="text-error" />
                    <h3 className="text-xl font-bold text-foreground">Common Pain Points</h3>
                  </div>
                  <ul className="space-y-6">
                    {location.challenges.map((c, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <div className="mt-1 bg-error/10 text-error rounded-full p-1 flex-shrink-0">
                          <Icon name="X" size={14} />
                        </div>
                        <p className="text-text-secondary text-sm md:text-base leading-relaxed">{c}</p>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Solutions Card */}
                <div className="p-8 bg-card border border-border rounded-3xl">
                  <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-border">
                    <Icon name="CheckCircle" size={24} className="text-success" />
                    <h3 className="text-xl font-bold text-foreground">Our Strategy</h3>
                  </div>
                  <ul className="space-y-6">
                    {location.solutions.map((s, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <div className="mt-1 bg-success/10 text-success rounded-full p-1 flex-shrink-0">
                          <Icon name="Check" size={14} />
                        </div>
                        <p className="text-text-secondary text-sm md:text-base leading-relaxed">{s}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Related Services Cards */}
          <section className="py-20 max-w-6xl mx-auto px-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">
                  Core Development Services
                </h2>
                <p className="text-text-secondary text-xs md:text-sm">
                  Click through our detailed services pages to learn more about our process.
                </p>
              </div>
              <Link to="/services" className="text-primary hover:text-accent font-semibold text-sm transition-colors mt-4 md:mt-0 flex items-center space-x-1">
                <span>View All Services</span>
                <Icon name="ArrowRight" size={14} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {matchedServices.map((srv) => (
                <div key={srv.id} className="p-6 bg-card border border-border hover:border-primary/40 rounded-2xl hover:shadow-brand-md transition-all duration-300 flex flex-col justify-between h-64">
                  <div>
                    <div className="w-10 h-10 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-4">
                      <Icon name={srv.icon} size={20} />
                    </div>
                    <h4 className="font-bold text-lg text-foreground mb-2">{srv.title}</h4>
                    <p className="text-text-secondary text-xs md:text-sm line-clamp-3 leading-relaxed">
                      {srv.tagline}
                    </p>
                  </div>
                  <Link to={`/services/${srv.id}`} className="text-primary hover:text-accent text-xs font-semibold flex items-center space-x-1 mt-4">
                    <span>Learn More</span>
                    <Icon name="ArrowRight" size={12} />
                  </Link>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ Accordion Section */}
          <section className="py-20 bg-muted/20 border-t border-border">
            <div className="max-w-4xl mx-auto px-6">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-text-secondary text-xs md:text-sm">
                  Get quick answers about our regional processes and pricing workflows.
                </p>
              </div>

              <div className="space-y-4">
                {location.faq.map((faq, index) => (
                  <div key={index} className="border border-border rounded-xl bg-card overflow-hidden transition-all duration-300">
                    <button
                      onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-5 text-left font-semibold text-foreground hover:bg-muted/30 transition-colors"
                    >
                      <span>{faq.q}</span>
                      <Icon
                        name="ChevronDown"
                        size={18}
                        className={`text-text-secondary transition-transform duration-300 ${activeFaq === index ? 'rotate-180 text-primary' : ''}`}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {activeFaq === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                        >
                          <div className="p-5 pt-0 border-t border-border/40 text-text-secondary text-sm md:text-base leading-relaxed whitespace-pre-line">
                            {faq.a}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Final CTA Section */}
          <section className="py-20 bg-gradient-to-br from-primary/5 via-accent/5 to-success/5 border-t border-border">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Ready to Build Your Custom System?
              </h2>
              <p className="text-lg md:text-xl text-text-secondary mb-8 max-w-xl mx-auto leading-relaxed">
                Connect directly with our full-stack developers to outline your system details. Pay once, own the source code forever.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={openConsultation}
                  className="inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-primary to-accent text-white px-8 py-4 rounded-xl font-semibold hover:shadow-brand-lg transition-all duration-300 transform hover:scale-105"
                >
                  <Icon name="Rocket" size={20} />
                  <span>Start Your Project</span>
                </button>
                <Link
                  to="/contact-engagement-hub"
                  className="inline-flex items-center justify-center space-x-2 bg-card border border-border text-foreground px-8 py-4 rounded-xl font-semibold hover:bg-muted transition-all duration-300"
                >
                  <Icon name="Calendar" size={20} />
                  <span>Contact Our Team</span>
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default LocationDetailPage;
